/**
 * EditConversationModal Component
 * Modal for editing a conversation's name and advertiser
 */

import { useState, useEffect } from 'react'
import { X, Pencil } from 'lucide-react'
import { Conversation } from '../../types'
import { AdvertiserSelect } from '../shared/AdvertiserSelect'
import { Advertiser } from '../../lib/api/advertisers'
import { isUserMessage, ChatHistoryItem } from '../../lib/actionCable'

interface EditConversationModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { query: string; advertiser_id?: number }) => Promise<void>
  isLoading?: boolean
  conversation: Conversation | null
}

export function EditConversationModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  conversation
}: EditConversationModalProps) {
  const [name, setName] = useState('')
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Pre-fill form when conversation changes
  useEffect(() => {
    if (conversation && isOpen) {
      // Set name from query or first user message
      const firstUserMsg = conversation.chat_history?.find(msg => isUserMessage(msg as ChatHistoryItem))
      const conversationName = conversation.query || 
        firstUserMsg?.message || firstUserMsg?.content || 
        ''
      setName(conversationName)
      
      // Pre-fill advertiser from conversation data (API now includes full advertiser object)
      if (conversation.advertiser) {
        setSelectedAdvertiser(conversation.advertiser as Advertiser)
      } else {
        setSelectedAdvertiser(null)
      }
    }
  }, [conversation, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate name
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Conversation name is required')
      return
    }

    if (trimmedName.length > 500) {
      setError('Name must be 500 characters or less')
      return
    }

    try {
      await onSubmit({
        query: trimmedName,
        advertiser_id: selectedAdvertiser?.id
      })
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update conversation')
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setName('')
      setSelectedAdvertiser(null)
      setError(null)
      onClose()
    }
  }

  if (!isOpen || !conversation) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                <Pencil className="h-3 w-3" />
                Conversation
              </div>
              <h2 className="text-2xl font-bold mt-1">Edit conversation</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Update the conversation name and associated advertiser.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Conversation Name */}
          <div>
            <label
              htmlFor="conversation-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="conversation-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter conversation name"
              disabled={isLoading}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {name.length}/500 characters
            </p>
          </div>

          {/* Advertiser Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Advertiser
            </label>
            <AdvertiserSelect
              value={selectedAdvertiser}
              onChange={setSelectedAdvertiser}
              placeholder="Search and select an advertiser..."
              disabled={isLoading}
            />
          </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

